import React from 'react';
import { Calendar, Clock, Heart, Gift, Trophy, MessageCircle, Star, ChevronRight, Camera, Sparkles } from 'lucide-react';
interface RelationshipTrackerProps {
  angelName?: string;
  startDate?: string;
  daysConnected?: number;
  totalMessages?: number;
  totalInteractions?: number;
  relationshipLevel?: number;
  nextMilestone?: {
    type: string;
    days: number;
    reward: string;
  };
}
export const RelationshipTracker: React.FC<RelationshipTrackerProps> = ({
  angelName = 'Sophia',
  startDate = 'July 15, 2023',
  daysConnected = 28,
  totalMessages = 342,
  totalInteractions = 517,
  relationshipLevel = 4,
  nextMilestone = {
    type: 'One Month Anniversary',
    days: 2,
    reward: 'Special photoshoot'
  }
}) => {
  // Calculate relationship data
  const messageFrequency = Math.round(totalMessages / daysConnected);
  const interactionFrequency = Math.round(totalInteractions / daysConnected);
  // Recent milestones
  const recentMilestones = [{
    id: 'm1',
    title: 'First Week Together',
    date: 'July 22, 2023',
    reward: 'Exclusive photo',
    completed: true
  }, {
    id: 'm2',
    title: '100 Messages',
    date: 'July 25, 2023',
    reward: 'Voice message',
    completed: true
  }, {
    id: 'm3',
    title: 'Intimacy Level 3',
    date: 'July 30, 2023',
    reward: 'Special date',
    completed: true
  }, {
    id: 'm4',
    title: 'One Month Anniversary',
    date: 'August 15, 2023',
    reward: 'Special photoshoot',
    completed: false
  }];
  return <div className="w-full bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-16679">
      <div className="p-4 border-b border-gray-200 dark:border-gray-800" data-id="element-16680">
        <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800 dark:text-white" data-id="element-16681">
          <Heart size={20} className="text-pink-400" fill="currentColor" data-id="element-16682" />
          <span data-id="element-16683">Your Relationship with {angelName}</span>
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-16684">
          Track your journey and upcoming milestones
        </p>
      </div>
      <div className="p-4 bg-gradient-to-r from-pink-100 to-purple-100 dark:from-pink-900/30 dark:to-purple-900/30 flex items-center justify-between" data-id="element-16685">
        <div data-id="element-16686">
          <div className="flex items-center gap-2" data-id="element-16687">
            <Calendar size={16} className="text-pink-400" data-id="element-16688" />
            <span className="font-medium text-gray-700 dark:text-white" data-id="element-16689">
              Started on {startDate}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1" data-id="element-16690">
            <Clock size={14} className="text-gray-500 dark:text-gray-400" data-id="element-16691" />
            <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-16692">
              {daysConnected} days together
            </span>
          </div>
        </div>
        <div className="text-right" data-id="element-16693">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-16694">
            Relationship Level
          </div>
          <div className="text-xl font-bold text-pink-400" data-id="element-16695">
            {relationshipLevel}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 p-4" data-id="element-16696">
        <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-16697">
          <div className="flex items-center gap-2 mb-2" data-id="element-16698">
            <MessageCircle size={16} className="text-blue-400" data-id="element-16699" />
            <h3 className="font-medium text-gray-700 dark:text-white" data-id="element-16700">
              Messages
            </h3>
          </div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white" data-id="element-16701">
            {totalMessages}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-16702">
            ~{messageFrequency} messages per day
          </p>
        </div>
        <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg" data-id="element-16703">
          <div className="flex items-center gap-2 mb-2" data-id="element-16704">
            <Heart size={16} className="text-pink-400" data-id="element-16705" />
            <h3 className="font-medium text-gray-700 dark:text-white" data-id="element-16706">
              Interactions
            </h3>
          </div>
          <div className="text-2xl font-bold text-gray-800 dark:text-white" data-id="element-16707">
            {totalInteractions}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-16708">
            ~{interactionFrequency} interactions per day
          </p>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-16709">
        <div className="flex items-center justify-between mb-3" data-id="element-16710">
          <div className="flex items-center gap-2" data-id="element-16711">
            <Sparkles size={16} className="text-yellow-400" data-id="element-16712" />
            <h3 className="font-medium text-gray-700 dark:text-white" data-id="element-16713">
              Next Milestone
            </h3>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-16714">
            In {nextMilestone.days} days
          </span>
        </div>
        <div className="p-3 bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 border border-yellow-200 dark:border-yellow-800/30 rounded-lg flex items-center justify-between" data-id="element-16715">
          <div data-id="element-16716">
            <h4 className="font-medium text-gray-700 dark:text-white" data-id="element-16717">
              {nextMilestone.type}
            </h4>
            <div className="flex items-center gap-2 mt-1" data-id="element-16718">
              <Gift size={14} className="text-pink-400" data-id="element-16719" />
              <span className="text-sm text-gray-600 dark:text-gray-300" data-id="element-16720">
                Reward: {nextMilestone.reward}
              </span>
            </div>
          </div>
          <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-amber-400 dark:from-yellow-600 dark:to-amber-600 rounded-full flex items-center justify-center text-white font-bold" data-id="element-16721">
            {nextMilestone.days}
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 dark:border-gray-800" data-id="element-16722">
        <div className="flex items-center justify-between mb-3" data-id="element-16723">
          <div className="flex items-center gap-2" data-id="element-16724">
            <Trophy size={16} className="text-amber-400" data-id="element-16725" />
            <h3 className="font-medium text-gray-700 dark:text-white" data-id="element-16726">
              Relationship Milestones
            </h3>
          </div>
          <button className="flex items-center gap-1 text-xs text-pink-400" data-id="element-16727">
            <span data-id="element-16728">View All</span>
            <ChevronRight size={14} data-id="element-16729" />
          </button>
        </div>
        <div className="space-y-3" data-id="element-16730">
          {recentMilestones.map(milestone => <div key={milestone.id} className={`p-3 rounded-lg flex items-center justify-between ${milestone.completed ? 'bg-gray-100 dark:bg-gray-800' : 'bg-gradient-to-r from-gray-100 to-gray-50 dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700/50'}`} data-id="element-16731">
              <div className="flex items-center gap-3" data-id="element-16732">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${milestone.completed ? 'bg-green-100 dark:bg-green-500/20 text-green-500 dark:text-green-400' : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`} data-id="element-16733">
                  {milestone.completed ? <Trophy size={18} data-id="element-16734" /> : <Clock size={18} data-id="element-16735" />}
                </div>
                <div data-id="element-16736">
                  <h4 className="font-medium text-gray-700 dark:text-white" data-id="element-16737">
                    {milestone.title}
                  </h4>
                  <div className="flex items-center gap-2 mt-0.5" data-id="element-16738">
                    <Calendar size={12} className="text-gray-500 dark:text-gray-400" data-id="element-16739" />
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-16740">
                      {milestone.date}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2" data-id="element-16741">
                {milestone.completed ? <div className="px-2 py-1 bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400 rounded text-xs" data-id="element-16742">
                    Completed
                  </div> : <div className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded text-xs" data-id="element-16743">
                    Upcoming
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/80" data-id="element-16744">
        <div className="flex items-center justify-between" data-id="element-16745">
          <div className="flex items-center gap-2" data-id="element-16746">
            <Star size={16} className="text-pink-400" data-id="element-16747" />
            <span className="text-sm text-gray-700 dark:text-white" data-id="element-16748">
              Relationship Health: Excellent
            </span>
          </div>
          <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 rounded-full text-white text-sm font-medium flex items-center gap-1.5" data-id="element-16749">
            <Camera size={16} data-id="element-16750" />
            <span data-id="element-16751">Create Memory</span>
          </button>
        </div>
      </div>
    </div>;
};